import React, { useCallback, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import SwiperClass from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import { EffectFade, Pagination } from 'swiper/modules';

import { Button, ButtonTypeMap, Grid2 as Grid, styled } from '@mui/material';
import { OverrideProps } from '@mui/material/OverridableComponent';

import ResponsiveGrid from '../../../layout/grid';
import styleVariables from '../../../styles/export-variables.module.scss';
import swiperClasses from '../../../styles/swiper-pagination.module.scss';
import { useAutoStartAnimation } from './auto-start-animation';

type SlideData<T extends React.ElementType> = {
  industryName: string;
  link: {
    props: OverrideProps<ButtonTypeMap, T>;
  };
  imageAltText?: string | null;
  image: React.ReactElement;
};

type Props<T extends React.ElementType> = {
  slides: SlideData<T>[];
  linkComponent: T;
};

export const RolodexStage = <T extends React.ElementType>({
  slides,
  linkComponent,
}: Props<T>) => {
  const [imageCaptionSwiper, setImageCaptionSwiper] =
    useState<SwiperClass | null>(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  const slideToIndex = useCallback(
    (index: number) => {
      if (imageCaptionSwiper) {
        imageCaptionSwiper.slideTo(index);
      }
    },
    [imageCaptionSwiper],
  );

  const slideNext = useCallback(() => {
    slideToIndex(((imageCaptionSwiper?.realIndex ?? 0) + 1) % slides.length);
  }, [imageCaptionSwiper, slides.length, slideToIndex]);

  const { pauseAnimation } = useAutoStartAnimation(slideNext);

  const onSlideChange = useCallback(
    (s: SwiperClass) => {
      setActiveItemIndex(s.realIndex);
    },
    [setActiveItemIndex],
  );

  return (
    <HeightContainer itemsCount={slides.length}>
      <TextContainer>
        <ResponsiveGrid>
          <CaptionContainer size={12}>
            {slides.map((slide, idx) => (
              <DefaultCaptionText
                onClick={() => {
                  pauseAnimation();
                  slideToIndex(idx);
                }}
                key={idx}
                className={idx === activeItemIndex ? 'highlight' : ''} //generateItemClassNames(idx)}
              >
                {slide.industryName}
              </DefaultCaptionText>
            ))}
            <StyledButton
              variant={'contained'}
              LinkComponent={linkComponent}
              {...slides[activeItemIndex].link.props}
            >
              {slides[activeItemIndex].industryName}
            </StyledButton>
          </CaptionContainer>
        </ResponsiveGrid>
      </TextContainer>
      <SwiperContainer>
        <Swiper
          style={{ height: '100%' }}
          modules={[EffectFade, Pagination]}
          effect="fade"
          speed={parseInt(styleVariables.transitionDuration)}
          normalizeSlideIndex
          allowSlideNext
          allowSlidePrev
          onSwiper={setImageCaptionSwiper}
          allowTouchMove={true}
          onSlideChange={onSlideChange}
          className={swiperClasses.swiper}
          pagination={{
            type: 'bullets',
            dynamicBullets: false,
            dynamicMainBullets: 1,
            clickable: true,
            renderBullet: (index, className) =>
              `<button 
                class="${swiperClasses.navigationDot} ${className}" 
                aria-label="navigate to slide ${index + 1}" 
                onclick="window.dispatchEvent(new Event('pauseAnimation'))"
              ></button>`,
          }}
        >
          {slides.map((slide, idx) => (
            <SwiperSlide key={idx} style={{ position: 'relative' }}>
              <Slide>{slide.image}</Slide>
              <GradientContainer />
            </SwiperSlide>
          ))}
        </Swiper>
      </SwiperContainer>
    </HeightContainer>
  );
};

const HeightContainer = styled('div')<{ itemsCount: number }>(({
  theme,
  itemsCount,
}) => {
  const DEFAULT_ITEM_COUNT_FOR_HEIGHT = 10;
  const additionHeightItems = Math.max(
    0,
    itemsCount - DEFAULT_ITEM_COUNT_FOR_HEIGHT,
  );

  return {
    position: 'relative',
    height: `calc(550px + ${additionHeightItems * 20}px)`,
    display: 'flex',

    [theme.breakpoints.up('sm')]: {
      height: `calc(574px + ${additionHeightItems * 20}px)`,
    },

    [theme.breakpoints.up('md')]: {
      height: `calc(660px + ${additionHeightItems * 24}px)`,
    },

    [theme.breakpoints.up('lg')]: {
      height: `calc(750px + ${additionHeightItems * 36}px)`,
    },
  };
});

const SwiperContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: '0 0 0 0',
  zIndex: 0,
}));

const TextContainer = styled('div')(({ theme }) => ({
  width: '100%',
  alignSelf: 'flex-end',
  marginBottom: 'calc(var(--spacer-l) + 15px)',
  zIndex: 1,
}));

const CaptionContainer = styled(Grid)(({ theme }) => ({
  padding: 'var(--spacer-l) 0 var(--spacer-xl)',
}));

const DefaultCaptionText = styled('button')(({ theme }) => ({
  ...theme.typography.body,
  cursor: 'pointer',
  margin: 0,
  display: 'block',
  backgroundColor: 'transparent',
  border: 'none',
  outline: 0,
  textAlign: 'left',
  marginBottom: 'var(--spacer-s)',
  color: 'var(--color-white)',
  opacity: 0.5,
  transition: `transform var(--transition-duration) ease-in-out, margin var(--transition-duration) ease-in-out, line-height var(--transition-duration) ease-in-out`,
  padding: 0,
  transformOrigin: 'left top',

  '&:last-of-type': {
    marginBottom: 0,
  },

  '--scale-highlight': '2.28',
  '--scale-highlight-2': '1.28',
  '--scale-highlight-3': '1.14',
  '--scale-highlight-4': '1.14',

  [theme.breakpoints.up('sm')]: {
    '--scale-highlight': '2.625',
    '--scale-highlight-2': '1.31',
    '--scale-highlight-3': '1.125',
    '--scale-highlight-4': '1.0',
  },

  [theme.breakpoints.up('md')]: {
    '--scale-highlight': '3.0',
    '--scale-highlight-2': '1.68',
    '--scale-highlight-3': '1.31',
    '--scale-highlight-4': '1.125',
  },

  [theme.breakpoints.up('lg')]: {
    '--scale-highlight': '3.55',
    '--scale-highlight-2': '1.77',
    '--scale-highlight-3': '1.5',
    '--scale-highlight-4': '1.166',
  },

  '&.highlight': {
    opacity: 1,
    transform: 'scale(var(--scale-highlight))',
    fontWeight: theme.typography.fontWeightLight,
    marginTop: 'var(--spacer-m)',
    marginBottom: 'calc(var(--spacer-m) * 3.7)',

    '&:first-of-type': {
      marginTop: 0,
    },
  },

  '.highlight + &, &:has(+ .highlight)': {
    opacity: 0.75,
    transform: 'scale(var(--scale-highlight-2))',
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: 'calc(var(--spacer-m) * 1.6)',
  },

  '.highlight + button + &, &:has(+ button + .highlight)': {
    opacity: 0.5,
    transform: 'scale(var(--scale-highlight-3))',
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: 'calc(var(--spacer-m) * 1.3)',
  },

  '.highlight + button + button + &, &:has(+ button + button + .highlight)': {
    opacity: 0.5,
    transform: 'scale(var(--scale-highlight-4))',
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: 'calc(var(--spacer-s))',
  },
}));

const Slide = styled('div')(({ theme }) => ({
  height: '100%',

  'img, .gatsby-image-wrapper': {
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    objectPosition: 'top',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: 'var(--spacer-l)',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const GradientContainer = styled('div')(({ theme }) => ({
  background:
    'linear-gradient(90deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%)',
  inset: '0 0 0 0',
  position: 'absolute',

  [theme.breakpoints.up('sm')]: {
    background:
      'linear-gradient(90deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 70%)',
  },
}));
